import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { useGridApiContext as usePremiumGridApiContext } from "@mui/x-data-grid-premium";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { useSnackbar } from "notistack";
import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import { ActionType, Actions } from "./DataGrid";
import {
  actionsMenuButtonSx,
  dropdownMenuItemSxFor,
  dropdownMenuListItemTextSx,
  dropdownMenuListProps,
  listActionsDropdownMenuItemSx,
  listActionsDropdownMenuPaperProps,
  listActionsDropdownSectionDividerSx,
  listActionsDropdownSectionLabelSx,
} from "../dropdownMenuStyles";

interface ActionMenuButtonProps {
  onClick: (event: any) => any;
}

const ActionMenuButton: React.FC<ActionMenuButtonProps> = (
  props: ActionMenuButtonProps
) => {
  const { onClick } = props;

  return (
    <Button
      aria-controls="options-menu"
      aria-haspopup="true"
      onClick={onClick}
      sx={actionsMenuButtonSx}
      variant="contained"
      endIcon={<KeyboardArrowDownIcon />}
    >
      Actions
    </Button>
  );
};

export interface ActionMenuItem {
  name: string;
  namePlural?: string;
  action?: [string, string];
  to?: string;
  onClick?: (arg0?: any) => void;
  beforeAction?: (arg0?: any) => boolean | Promise<boolean>;
  icon?: any;
  requiresSelection?: boolean;
  multipleSelection?: boolean;
  isReturnDataSelection?: boolean;
  preventMultiSelect?: boolean;
  multiSelectErrorMessage?: string;
  disabled?: boolean;
  disabledPredicate?: (selectedRows: any[]) => boolean;
  /**
   * Why this entry is disabled, surfaced as a tooltip on the greyed item.
   *
   * A disabled control with no stated reason reads as a bug. Use this when the
   * entry exists for SHAPE — the surface it belongs to is real and another lane
   * or a missing backend route owns the action — so the reader learns why it is
   * inert instead of filing it as broken. Pattern 340 §2b.
   *
   * Honoured by `inspections-tr/AircraftInspectionLimitations.tsx`'s fork (a
   * tooltip) and, since H104 (2026-09-28, the aircraft ADs / SBs tabs), by the
   * canonical menu below: a disabled entry with a reason renders the reason as
   * its secondary line — visible without hovering, since a disabled MenuItem
   * takes no pointer events — and renders even with no onClick / action / to,
   * because a shape-only entry has nothing to run.
   */
  disabledReason?: string;
  hidden?: boolean;
  hiddenPredicate?: (selectedRows: any[]) => boolean;
}

/** Subheader with nested action items (e.g. "Print" with "Picking Slip", "Parts Request List") */
export interface ActionMenuSubheader {
  subheader: string;
  children: ActionMenuItem[];
}

export type ActionMenuEntry = ActionMenuItem | ActionMenuSubheader;

function isActionMenuSubheader(
  entry: ActionMenuEntry
): entry is ActionMenuSubheader {
  return "subheader" in entry && "children" in entry;
}

interface ActionMenuProps {
  actions?: ActionMenuEntry[];
  setAction: (action: ActionType) => void;
  setSelected?: (arg0: any) => void;
  setSelectionModel?: (arg0: any) => void;
}

/** Subset of the grid API both Pro v6 and Premium v7 expose for row selection. */
type SelectionApiRef = {
  current: {
    getSelectedRows: () => {
      values: () => IterableIterator<any>;
      size: number;
    };
  };
};

function ActionMenuView(
  props: ActionMenuProps & { apiRef: SelectionApiRef }
) {
  const { actions, apiRef } = props;
  const { enqueueSnackbar } = useSnackbar();

  const popupState = usePopupState({
    variant: "popover",
    popupId: "action-menu",
  });

  if (actions == null || actions.length === 0) {
    return null;
  }

  /**
   * The payload's ticked rows: an array, which also answers `.next()` like the
   * Map-values iterator it replaced (8e2d1cd9c, 2026-09-18). Handlers written
   * against the iterator (`selectedRows.next().value`: PO Authorise and
   * Duplicate, the Flights / Pilot Roster / Flight Expenses Authorise, Edit
   * on Flights and Modules, …) threw "selectedRows.next is not a function".
   */
  const payloadRows = (list: any[]) => {
    const iterator = list.values();
    return Object.defineProperty(list.slice(), "next", {
      value: () => iterator.next(),
      enumerable: false,
    });
  };

  const getSelectedRowsList = () => {
    try {
      // A selected id can outlive its row after a filtered workflow update.
      return Array.from(apiRef.current?.getSelectedRows()?.values() ?? []).filter(row => row != null);
    } catch {
      return [];
    }
  };

  const onClick = (action: ActionMenuItem) => {
    const selectedList = getSelectedRowsList();
    const isDisabled =
      Boolean(action.disabled) ||
      (action.disabledPredicate ? action.disabledPredicate(selectedList) : false);
    if (isDisabled) return;

    popupState.close();
    // get the first selected row
    const selected = selectedList[0];

    if (action.requiresSelection && !selected) {
      enqueueSnackbar("Select a row to continue", {
        variant: "warning",
      });
      return;
    }

    if (
      !action.multipleSelection &&
      selectedList.length > 1
    ) {
      if (action.preventMultiSelect) {
        enqueueSnackbar(
          action.multiSelectErrorMessage || "Please select one row only.",
          {
            variant: "warning",
          }
        );
        return;
      }
      enqueueSnackbar("Using first selected row", {
        variant: "info",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    const payload = {
      selectedRows: payloadRows(selectedList),
      setSelectionModel: props.setSelectionModel,
      apiRef: apiRef,
    };
    if (action.action) {
      if (action.beforeAction) {
        const canContinue = action.beforeAction(payload);
        if (canContinue instanceof Promise) {
          canContinue.then((ok) => {
            if (!ok) return;
            if (action.multipleSelection) {
              if (props.setSelected) {
                props.setSelected(apiRef.current.getSelectedRows().values());
              }
              props.setAction(Actions[action.action![0]]);
            } else {
              if (props.setSelected) {
                props.setSelected(selected);
              }
              props.setAction(Actions[action.action![0]]);
            }
            if (action.onClick) {
              action.onClick(payload);
            }
          });
          return;
        }
        if (!canContinue) {
          return;
        }
      }
      if (action.multipleSelection) {
        if (props.setSelected) {
          props.setSelected(apiRef.current.getSelectedRows().values());
        }
        props.setAction(Actions[action.action[0]]);
      } else {
        if (props.setSelected) {
          props.setSelected(selected);
        }
        props.setAction(Actions[action.action[0]]);
      }
      if (action.onClick) {
        action.onClick(payload);
      }
    } else if (action.onClick) {
      // Handle custom actions with selection data but no action type
      const payload = {
        selectedRows: payloadRows(selectedList),
        setSelectionModel: props.setSelectionModel,
        apiRef: apiRef,
      };
      action.onClick(payload);
    }
  };

  const labelFor = (action: ActionMenuItem) =>
    action.multipleSelection &&
    apiRef.current.getSelectedRows().size > 1 &&
    action.namePlural
      ? action.namePlural
      : action.name;

  // One row style for top-level and sectioned items: the Figma list ACTIONS
  // menu (upstream 1a82588e4).
  const renderActionMenuItem = (action: ActionMenuItem, key: string) => {
    const itemSx = listActionsDropdownMenuItemSx;
    const selectedList = getSelectedRowsList();
    const isDisabled =
      Boolean(action.disabled) ||
      (action.disabledPredicate ? action.disabledPredicate(selectedList) : false);
    const isHidden =
      Boolean(action.hidden) ||
      (action.hiddenPredicate ? action.hiddenPredicate(selectedList) : false);

    if (isHidden) return null;

    if (isDisabled && action.disabledReason) {
      return (
        <MenuItem disabled={isDisabled} sx={dropdownMenuItemSxFor(itemSx, action.disabledReason)} key={key}>
          <ListItemText
            sx={dropdownMenuListItemTextSx}
            primary={action.name}
            secondary={action.disabledReason}
          />
        </MenuItem>
      );
    }

    if (action.action) {
      return (
        <MenuItem
          disabled={isDisabled}
          sx={itemSx}
          onClick={() => onClick(action)}
          key={key}
        >
          {action.icon ? (
            <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
              <action.icon fontSize="small" />
            </ListItemIcon>
          ) : null}
          <ListItemText sx={dropdownMenuListItemTextSx} primary={labelFor(action)} />
        </MenuItem>
      );
    }
    if (action.to != null) {
      return (
        <MenuItem
          disabled={isDisabled}
          sx={itemSx}
          onClick={() => {
            if (isDisabled) return;
            popupState.close();
          }}
          component={isDisabled ? "li" : Link}
          to={isDisabled ? undefined : action.to}
          key={key}
        >
          <ListItemText sx={dropdownMenuListItemTextSx} primary={action.name} />
        </MenuItem>
      );
    }
    if (action.onClick) {
      return (
        <MenuItem
          disabled={isDisabled}
          sx={itemSx}
          onClick={() => onClick(action)}
          key={key}
        >
          {action.icon ? (
            <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
              <action.icon fontSize="small" />
            </ListItemIcon>
          ) : null}
          <ListItemText sx={dropdownMenuListItemTextSx} primary={labelFor(action)} />
        </MenuItem>
      );
    }
    return null;
  };

  return (
    <div>
      <ActionMenuButton {...bindTrigger(popupState)} />
      <Menu
        {...bindMenu(popupState)}
        PaperProps={listActionsDropdownMenuPaperProps}
        MenuListProps={dropdownMenuListProps}
      >
        {actions.map((entry, index) => {
          if (isActionMenuSubheader(entry)) {
            return (
              <Fragment key={index}>
                <Box
                  component="li"
                  aria-hidden
                  sx={listActionsDropdownSectionDividerSx}
                />
                <ListSubheader
                  className="list-subheader-item"
                  sx={listActionsDropdownSectionLabelSx}
                >
                  {entry.subheader}
                </ListSubheader>
                {entry.children.map((action, childIndex) =>
                  renderActionMenuItem(action, `${index}-${childIndex}`)
                )}
              </Fragment>
            );
          }
          return renderActionMenuItem(entry, String(index));
        })}
      </Menu>
    </div>
  );
}

/** List ACTIONS menu for DataGridPro. Uses the Pro grid API context. */
export function ActionMenu(props: ActionMenuProps) {
  const apiRef = useGridApiContext();
  return <ActionMenuView {...props} apiRef={apiRef} />;
}

/**
 * Same menu inside DataGridPremium. Pro v6 and Premium v7 keep separate
 * React contexts, so this hook must come from the Premium package.
 */
export function ActionMenuPremium(props: ActionMenuProps) {
  const apiRef = usePremiumGridApiContext();
  return <ActionMenuView {...props} apiRef={apiRef} />;
}

export interface ActionMenuForRowsProps extends Omit<ActionMenuProps, "setAction"> {
  /** The ticks this menu acts on — the caller owns them, not a grid. */
  selectedRows: any[];
  setAction?: (action: ActionType) => void;
}

const noAction = () => undefined;

/**
 * The same menu OUTSIDE a grid, for a surface whose ONE ACTIONS sits in its
 * header and acts on a toolbar-less nested grid's ticks (BP-80 "One ACTIONS",
 * Aaron 2026-10-08: the Production slide-in). `selectedRows` stands in for the
 * grid's selection, so requiresSelection, disabledPredicate, namePlural and the
 * click payload behave as they do inside a grid. Needs no grid context.
 */
export function ActionMenuForRows(props: ActionMenuForRowsProps) {
  const { selectedRows, setAction, ...rest } = props;
  const apiRef = useMemo<SelectionApiRef>(() => {
    const byId = new Map(selectedRows.map((row, i) => [row?.id ?? i, row]));
    return { current: { getSelectedRows: () => byId } };
  }, [selectedRows]);
  return <ActionMenuView {...rest} setAction={setAction ?? noAction} apiRef={apiRef} />;
}

export default ActionMenu;
